import React, { useState, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import dayjs from 'dayjs';
import weekday from 'dayjs/plugin/weekday';
import isToday from 'dayjs/plugin/isToday';
import { parseDateOnly } from '../utils/dateUtils';
import { useMarkRecordFormDirty } from '../host/recordFormDirty';
dayjs.extend(weekday);
dayjs.extend(isToday);

const REFERENCE_DATE = '2000-01-01'; // fixed date for time-only values
const DAYJS_DATE_FORMAT = 'MM/DD/YYYY';
const VIEWPORT_PADDING = 8;
const POPUP_GAP = 4;
const TIME_MENU_ESTIMATED_HEIGHT = 180;
const DATETIME_MENU_ESTIMATED_HEIGHT = 300;
const DATETIME_MENU_MIN_WIDTH = 370;
const DATE_MENU_ESTIMATED_HEIGHT = 380;
const CALENDAR_MIN_WIDTH = 280;

/**
 * Viewport position for portaled menus. When opening above, anchor with `bottom`
 * so the panel sits flush on the trigger — do not assume estimated height
 * (`top = rect.top - 380` left a large gap when the calendar was shorter).
 */
const computePopupPosition = (rect, popupWidth, estimatedHeight) => {
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  const maxLeft = Math.max(VIEWPORT_PADDING, viewportWidth - popupWidth - VIEWPORT_PADDING);
  const left = Math.min(Math.max(rect.left, VIEWPORT_PADDING), maxLeft);

  const spaceBelow = viewportHeight - rect.bottom - VIEWPORT_PADDING;
  const spaceAbove = rect.top - VIEWPORT_PADDING;
  const placeAbove = spaceBelow < estimatedHeight && spaceAbove > spaceBelow;
  const gap = placeAbove ? 1 : POPUP_GAP;

  if (placeAbove) {
    return {
      left,
      width: popupWidth,
      placeAbove: true,
      bottom: viewportHeight - rect.top + gap,
      maxHeight: Math.max(120, spaceAbove - gap),
    };
  }
  return {
    left,
    width: popupWidth,
    placeAbove: false,
    top: rect.bottom + gap,
    maxHeight: Math.max(120, spaceBelow - gap),
  };
};

const popupPlacementStyle = (pos) => {
  if (!pos) return {};
  return {
    position: 'fixed',
    left: pos.left,
    width: pos.width,
    ...(pos.placeAbove
      ? { bottom: pos.bottom, top: 'auto' }
      : { top: pos.top, bottom: 'auto' }),
    ...(pos.maxHeight != null ? { maxHeight: pos.maxHeight } : {}),
  };
};

const MONTH_SHORT_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const PERIOD_MENU_ESTIMATED_HEIGHT = 420;
const FIELD_TRIGGER_HEIGHT = '44px';
const TIME_LIST_MAX_H = 112;
const HOURS_12 = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
const MINUTES_60 = Array.from({ length: 60 }, (_, i) => i);
const YEAR_MIN = 1900;
const YEAR_MAX = 2100;
const PERIOD_YEARS = Array.from({ length: YEAR_MAX - YEAR_MIN + 1 }, (_, i) => YEAR_MIN + i);

const popupPanelStyle = (pos) => ({
  ...popupPlacementStyle(pos),
  zIndex: 10000,
  background: 'var(--color-bg-alt)',
  color: 'var(--color-text)',
  border: '2px solid var(--color-accent)',
  borderRadius: 12,
  boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
  padding: 16,
  fontFamily: 'inherit',
  boxSizing: 'border-box',
  overflowY: 'auto',
});

const pickerCellStyle = (selected) => ({
  textAlign: 'center',
  padding: '14px 0',
  minHeight: 44,
  background: selected ? 'var(--color-accent)' : 'var(--color-bg-input)',
  color: selected ? '#fff' : 'var(--color-text)',
  borderRadius: 8,
  fontWeight: selected ? 700 : 500,
  fontSize: 14,
  cursor: 'pointer',
  border: '1px solid var(--color-border)',
  fontFamily: 'inherit',
  width: '100%',
});

function parsePeriodValue(value) {
  if (value == null || value === '') return { month: null, year: null };
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return { month: value.getMonth(), year: value.getFullYear() };
  }
  const s = String(value).trim();
  const ym = s.match(/^(\d{4})-(\d{1,2})$/);
  if (ym) {
    const month = parseInt(ym[2], 10) - 1;
    return {
      year: parseInt(ym[1], 10),
      month: month >= 0 && month <= 11 ? month : null
    };
  }
  const yOnly = s.match(/^(\d{4})$/);
  if (yOnly) return { year: parseInt(yOnly[1], 10), month: null };
  const mOnly = s.match(/^(\d{1,2})$/);
  if (mOnly) {
    const month = parseInt(mOnly[1], 10) - 1;
    return { month: month >= 0 && month <= 11 ? month : null, year: null };
  }
  const d = dayjs(value);
  if (d.isValid()) return { month: d.month(), year: d.year() };
  return { month: null, year: null };
}

function TimeScrollColumn({ label, items, value, onSelect, format = (v) => String(v), compact = false, variant = 'boxed' }) {
  const listRef = React.useRef(null);
  const selectedRef = React.useRef(null);
  const inline = variant === 'inline';

  React.useLayoutEffect(() => {
    const list = listRef.current;
    const node = selectedRef.current;
    if (!list || !node) return;
    const listRect = list.getBoundingClientRect();
    const nodeRect = node.getBoundingClientRect();
    list.scrollTop += nodeRect.top - listRect.top - (listRect.height / 2) + (nodeRect.height / 2);
  }, [value]);

  return (
    <div
      className={`cdp-time-col${compact ? ' cdp-time-col--ampm' : ''}`}
      style={{ flex: compact ? (inline ? '0 0 42px' : '0 0 76px') : 1, minWidth: 0, height: inline && !compact ? '100%' : undefined }}
    >
      {!inline && (
        <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginBottom: 4, textAlign: 'center' }}>{label}</div>
      )}
      <div
        ref={listRef}
        className={`cdp-time-list${compact ? ' cdp-time-list--ampm' : ''}${inline ? ' cdp-time-list--inline' : ''}`}
        onWheel={(e) => e.stopPropagation()}
        style={inline ? undefined : {
          maxHeight: compact ? undefined : TIME_LIST_MAX_H,
          height: compact ? TIME_LIST_MAX_H : undefined,
          overflowY: compact ? 'hidden' : 'auto',
          border: '1px solid var(--color-border)',
          borderRadius: 8,
          background: 'var(--color-bg-input)',
        }}
      >
        {items.map((item) => (
          <button
            key={String(item)}
            type="button"
            ref={item === value ? selectedRef : null}
            className={`cdp-time-list-item${item === value ? ' is-selected' : ''}`}
            onMouseDown={(e) => {
              e.preventDefault();
              onSelect(item);
            }}
          >
            {format(item)}
          </button>
        ))}
      </div>
    </div>
  );
}

function ShortTimeLists({ hour12, minute, amPm, onChange, variant = 'boxed' }) {
  return (
    <div
      className={`cdp-time-lists${variant === 'inline' ? ' cdp-time-lists--inline' : ''}`}
      style={{ display: 'flex', gap: variant === 'inline' ? 2 : 8, alignItems: 'stretch', justifyContent: 'center', height: variant === 'inline' ? '100%' : undefined }}
    >
      <TimeScrollColumn
        label="Hour"
        items={HOURS_12}
        value={hour12}
        onSelect={(h) => onChange(h, minute, amPm)}
        format={(h) => String(h).padStart(2, '0')}
        variant={variant}
      />
      <TimeScrollColumn
        label="Min"
        items={MINUTES_60}
        value={minute}
        onSelect={(m) => onChange(hour12, m, amPm)}
        format={(m) => String(m).padStart(2, '0')}
        variant={variant}
      />
      <TimeScrollColumn
        label="AM/PM"
        items={['AM', 'PM']}
        value={amPm}
        onSelect={(ap) => onChange(hour12, minute, ap)}
        compact
        variant={variant}
      />
    </div>
  );
}

/** Map user-facing tokens (MM/dd/yyyy) to dayjs format tokens (MM/DD/YYYY). */
const toDayjsFormat = (formatStr) => {
  if (!formatStr) return DAYJS_DATE_FORMAT;
  return String(formatStr).replace(/yyyy/g, 'YYYY').replace(/dd/g, 'DD');
};

const toDayjsDateOnly = (dateValue) => {
  const parsed = parseDateOnly(dateValue);
  return parsed ? dayjs(parsed) : null;
};

const CustomDatePicker = ({ label, value, onChange, placeholder = 'MM/dd/yyyy', disabled = false, style = {}, width = '100%', height = '58px', format = 'MM/DD/YYYY', showTimeOnly = false, showDateTime = false, showMonthYear = false, showYearOnly = false, showMonthOnly = false, required = false, error = false, helperText = '', htmlRequired }) => {
  const markRecordDirty = useMarkRecordFormDirty();
  const emitChange = (...args) => {
    markRecordDirty();
    if (typeof onChange === 'function') onChange(...args);
  };
  const dayjsFormat = toDayjsFormat(format);
  const [open, setOpen] = useState(false);
  const [calendarPosition, setCalendarPosition] = useState(null);
  const inputRef = useRef(null);
  const calendarRef = useRef(null);
  const blurFromCalendarClickRef = useRef(false);
  const textInputRef = useRef(null);
  // Keep keystrokes while focused even if the parent stores a Date/null.
  const [typedValue, setTypedValue] = useState(null);
  // Calendar state
  const initialDate = toDayjsDateOnly(value) || dayjs();
  const [calendarMonth, setCalendarMonth] = useState(initialDate.month());
  const [calendarYear, setCalendarYear] = useState(initialDate.year());
  // Parse selected date
  const selectedDate = toDayjsDateOnly(value);
  // DateTime mode: working value (date + time) when popup is open
  const [dateTimeOpen, setDateTimeOpen] = useState(false);
  const [dateTimeMenuPosition, setDateTimeMenuPosition] = useState(null);
  const dateTimeTriggerRef = useRef(null);
  const dateTimeMenuRef = useRef(null);
  const dateTimeInitial = value && dayjs(value).isValid() ? dayjs(value) : dayjs();
  const [dateTimeWorking, setDateTimeWorking] = useState(dateTimeInitial);
  const [dtCalendarMonth, setDtCalendarMonth] = useState(dateTimeInitial.month());
  const [dtCalendarYear, setDtCalendarYear] = useState(dateTimeInitial.year());
  const dtParts = value && dayjs(value).isValid() ? (() => { const d = dayjs(value); const h24 = d.hour(); const hour12 = h24 === 0 ? 12 : h24 > 12 ? h24 - 12 : h24; return { hour12, minute: d.minute(), amPm: h24 < 12 ? 'AM' : 'PM' }; })() : { hour12: 12, minute: 0, amPm: 'AM' };
  const [dtHour, setDtHour] = useState(dtParts.hour12);
  const [dtMinute, setDtMinute] = useState(dtParts.minute);
  const [dtAmPm, setDtAmPm] = useState(dtParts.amPm);

  // Format committed values for display. Do NOT loosely parse partial typed
  // strings with dayjs — that reformats mid-keystroke (cursor jumps) or returns
  // '' (clears the field), e.g. "06" → "06/01/2001", "06/0" → "".
  const formatDisplayValue = (dateValue) => {
    if (dateValue == null || dateValue === '') return '';

    if (typeof dateValue === 'string') {
      const trimmed = dateValue.trim();
      if (!trimmed) return '';

      if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(trimmed)) {
        const parsed = parseDateOnly(trimmed);
        return parsed ? dayjs(parsed).format(DAYJS_DATE_FORMAT) : trimmed;
      }

      if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
        const [year, month, day] = trimmed.split('T')[0].split('-');
        return `${month}/${day}/${year}`;
      }

      // Keep in-progress typed text as-is (digits/slashes, incomplete dates).
      return trimmed;
    }

    if (dateValue instanceof Date) {
      if (isNaN(dateValue.getTime())) return '';
      const parsed = parseDateOnly(dateValue);
      return parsed ? dayjs(parsed).format(DAYJS_DATE_FORMAT) : '';
    }
    return '';
  };

  const formattedValue = formatDisplayValue(value);
  const displayValue = typedValue != null ? typedValue : formattedValue;

  React.useEffect(() => {
    if (typedValue == null) return;
    if (textInputRef.current && document.activeElement === textInputRef.current) return;
    setTypedValue(null);
  }, [value, typedValue]);

  const syncCalendarToValue = () => {
    const d = toDayjsDateOnly(value) || dayjs();
    setCalendarMonth(d.month());
    setCalendarYear(d.year());
  };

  // Handle input click/focus to open calendar
  const handleInputFocus = () => {
    if (!disabled) {
      syncCalendarToValue();
      setOpen(true);
    }
  };

  const commitTypedDate = (raw) => {
    let val = String(raw ?? '').trim();
    if (!val) {
      emitChange('');
      return;
    }

    val = val.replace(/-/g, '/');
    const digits = val.replace(/\D/g, '');
    const slashShort = /^(\d{1,2})\/(\d{1,2})\/(\d{2})$/.exec(val);

    if (slashShort) {
      const yyyy = 2000 + parseInt(slashShort[3], 10);
      val = `${slashShort[1].padStart(2, '0')}/${slashShort[2].padStart(2, '0')}/${yyyy}`;
    } else if (/^\d{6}$/.test(digits) && !/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(val)) {
      val = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${2000 + parseInt(digits.slice(4), 10)}`;
    } else if (/^\d{8}$/.test(digits)) {
      val = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
    }

    const parsed = parseDateOnly(val);
    if (parsed) {
      emitChange(dayjs(parsed).format(DAYJS_DATE_FORMAT));
    }
  };

  // Sync datetime working state when value changes (e.g. load record)
  React.useEffect(() => {
    if (!showDateTime) return;
    const d = value && dayjs(value).isValid() ? dayjs(value) : dayjs();
    setDateTimeWorking(d);
    setDtCalendarMonth(d.month());
    setDtCalendarYear(d.year());
    const h24 = d.hour();
    setDtHour(h24 === 0 ? 12 : h24 > 12 ? h24 - 12 : h24);
    setDtMinute(d.minute());
    setDtAmPm(h24 < 12 ? 'AM' : 'PM');
  }, [showDateTime, value]);

  // Handle outside click to close calendar
  React.useEffect(() => {
    if (showTimeOnly || showDateTime || showMonthYear || showYearOnly || showMonthOnly) return;
    function handleClickOutside(event) {
      const isClickInsideInput = inputRef.current && inputRef.current.contains(event.target);
      const isClickInsideCalendar = calendarRef.current && calendarRef.current.contains(event.target);
      
      if (!isClickInsideInput && !isClickInsideCalendar) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside, true);
    } else {
      document.removeEventListener('mousedown', handleClickOutside, true);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside, true);
  }, [open, showTimeOnly, showDateTime, showMonthYear, showYearOnly, showMonthOnly]);

  // Time-only: format value for display (12h with AM/PM)
  const formatTimeDisplay = (dateValue) => {
    if (dateValue == null || dateValue === '') return '';
    const d = dayjs(dateValue);
    if (!d.isValid()) return '';
    return d.format('h:mm A');
  };
  const timeDisplayValue = showTimeOnly ? formatTimeDisplay(value) : '';

  // Time-only: parse value to hour (1-12), minute (0-59), amPm ('AM'|'PM')
  const getTimeParts = (dateValue) => {
    if (dateValue == null || dateValue === '') return { hour12: 12, minute: 0, amPm: 'AM' };
    const d = dayjs(dateValue);
    if (!d.isValid()) return { hour12: 12, minute: 0, amPm: 'AM' };
    const h24 = d.hour();
    const m = d.minute();
    const hour12 = h24 === 0 ? 12 : h24 > 12 ? h24 - 12 : h24;
    const amPm = h24 < 12 ? 'AM' : 'PM';
    return { hour12, minute: m, amPm };
  };

  const [timePickerOpen, setTimePickerOpen] = useState(false);
  const [timeMenuPosition, setTimeMenuPosition] = useState(null);
  const timePickerRef = useRef(null);
  const timeTriggerRef = useRef(null);
  const timeMenuRef = useRef(null);
  const initialParts = getTimeParts(value);
  const [timeHour, setTimeHour] = useState(initialParts.hour12);
  const [timeMinute, setTimeMinute] = useState(initialParts.minute);
  const [timeAmPm, setTimeAmPm] = useState(initialParts.amPm);
  // Editable input string for time (allows typing); syncs from value when value changes
  const [timeInputValue, setTimeInputValue] = useState(showTimeOnly ? formatTimeDisplay(value) : '');
  const effectiveRequired = htmlRequired === false
    ? false
    : Boolean(required || (typeof label === 'string' && label.includes('*')));

  // Sync time state and typed input when value prop changes (e.g. load record)
  React.useEffect(() => {
    if (!showTimeOnly) return;
    const parts = getTimeParts(value);
    setTimeHour(parts.hour12);
    setTimeMinute(parts.minute);
    setTimeAmPm(parts.amPm);
    setTimeInputValue(formatTimeDisplay(value));
  }, [showTimeOnly, value]);

  // Parse typed time string (12h "2:30 PM", "330pm", "4pm", or 24h "14:30") to Date or null
  const parseTypedTime = (str) => {
    if (!str || typeof str !== 'string') return null;
    const s = str.trim().toLowerCase();
    if (!s) return null;
    // 24h: HH:mm or H:mm (no am/pm)
    const m24 = s.match(/^(\d{1,2}):(\d{2})\s*$/);
    if (m24) {
      const h = parseInt(m24[1], 10);
      const min = parseInt(m24[2], 10);
      if (h >= 0 && h <= 23 && min >= 0 && min <= 59) {
        return dayjs(`${REFERENCE_DATE}T${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}:00`).toDate();
      }
    }
    // 12h with colon: "2:30 PM", "2:30 pm"
    const m12Colon = s.match(/^(\d{1,2}):(\d{2})\s*(am|pm)$/);
    if (m12Colon) {
      let h = parseInt(m12Colon[1], 10);
      const min = parseInt(m12Colon[2], 10);
      const ampm = m12Colon[3];
      if (h >= 1 && h <= 12 && min >= 0 && min <= 59) {
        if (ampm === 'pm' && h !== 12) h += 12;
        if (ampm === 'am' && h === 12) h = 0;
        return dayjs(`${REFERENCE_DATE}T${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}:00`).toDate();
      }
    }
    // 12h no colon: "1230pm" (2-digit hour + 2-digit min), "330pm"/"451am" (1-digit hour + 2-digit min), "4pm"/"12pm" (hour only)
    const m12Four = s.match(/^(\d{2})(\d{2})\s*(am|pm)$/);
    if (m12Four) {
      let h = parseInt(m12Four[1], 10);
      const min = parseInt(m12Four[2], 10);
      const ampm = m12Four[3];
      if (h >= 1 && h <= 12 && min >= 0 && min <= 59) {
        if (ampm === 'pm' && h !== 12) h += 12;
        if (ampm === 'am' && h === 12) h = 0;
        return dayjs(`${REFERENCE_DATE}T${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}:00`).toDate();
      }
    }
    const m12Three = s.match(/^(\d)(\d{2})\s*(am|pm)$/);
    if (m12Three) {
      let h = parseInt(m12Three[1], 10);
      const min = parseInt(m12Three[2], 10);
      const ampm = m12Three[3];
      if (h >= 1 && h <= 9 && min >= 0 && min <= 59) {
        if (ampm === 'pm' && h !== 12) h += 12;
        if (ampm === 'am' && h === 12) h = 0;
        return dayjs(`${REFERENCE_DATE}T${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}:00`).toDate();
      }
    }
    const m12HourOnly = s.match(/^(\d{1,2})\s*(am|pm)$/);
    if (m12HourOnly) {
      let h = parseInt(m12HourOnly[1], 10);
      const ampm = m12HourOnly[2];
      if (h >= 1 && h <= 12) {
        if (ampm === 'pm' && h !== 12) h += 12;
        if (ampm === 'am' && h === 12) h = 0;
        return dayjs(`${REFERENCE_DATE}T${String(h).padStart(2, '0')}:00:00`).toDate();
      }
    }
    // Hour only, no am/pm: "4", "12" -> default to AM (4:00 AM, 12:00 AM)
    const mHourOnly = s.match(/^(\d{1,2})$/);
    if (mHourOnly) {
      let h = parseInt(mHourOnly[1], 10);
      if (h >= 1 && h <= 12) {
        if (h === 12) h = 0; // 12 = midnight (12:00 AM)
        return dayjs(`${REFERENCE_DATE}T${String(h).padStart(2, '0')}:00:00`).toDate();
      }
    }
    // Fallback: dayjs with common time-only formats
    const d = dayjs(str.trim(), ['h:mm A', 'hh:mm A', 'h:mm a', 'H:mm', 'HH:mm'], true);
    if (d.isValid()) return dayjs(`${REFERENCE_DATE}T${d.format('HH:mm')}:00`).toDate();
    return null;
  };

  const handleTimeInputChange = (e) => {
    setTimeInputValue(e.target.value);
  };

  const handleTimeInputBlur = () => {
    const parsed = parseTypedTime(timeInputValue);
    if (parsed) {
      emitChange(parsed);
      setTimeInputValue(formatTimeDisplay(parsed));
    } else {
      setTimeInputValue(formatTimeDisplay(value));
    }
  };

  const handleTimeInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      setTimePickerOpen(false);
      const parsed = parseTypedTime(timeInputValue);
      if (parsed) {
        emitChange(parsed);
        setTimeInputValue(formatTimeDisplay(parsed));
      } else if (value != null && value !== '') {
        setTimeInputValue(formatTimeDisplay(value));
      }
    }
  };

  const applyTimeSelection = (hour, minute, amPm) => {
    let h24 = hour;
    if (amPm === 'PM' && hour !== 12) h24 = hour + 12;
    if (amPm === 'AM' && hour === 12) h24 = 0;
    const d = dayjs(`${REFERENCE_DATE}T${String(h24).padStart(2, '0')}:${String(minute).padStart(2, '0')}:00`);
    if (d.isValid()) emitChange(d.toDate());
  };

  const handleTimePickerClick = () => {
    if (disabled) return;
    setTimePickerOpen((o) => !o);
  };

  // Position time dropdown when open (for portal)
  useLayoutEffect(() => {
    if (!showTimeOnly || !timePickerOpen || !timeTriggerRef.current) return;
    const update = () => {
      if (timeTriggerRef.current) {
        const rect = timeTriggerRef.current.getBoundingClientRect();
        setTimeMenuPosition(computePopupPosition(rect, rect.width, TIME_MENU_ESTIMATED_HEIGHT));
      }
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
      setTimeMenuPosition(null);
    };
  }, [showTimeOnly, timePickerOpen]);

  // Position date calendar when open (portal so it doesn't create scrollbar in form).
  // Anchor to the input field, not the wrapper (wrapper includes the label and
  // made "open above" appear to float far away from the control).
  useLayoutEffect(() => {
    if (showTimeOnly || showDateTime || showMonthYear || showYearOnly || showMonthOnly || !open) return;
    const update = () => {
      const el = textInputRef.current || inputRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const popupWidth = Math.max(rect.width, CALENDAR_MIN_WIDTH);
      setCalendarPosition(computePopupPosition(rect, popupWidth, DATE_MENU_ESTIMATED_HEIGHT));
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
      setCalendarPosition(null);
    };
  }, [open, showTimeOnly, showDateTime, showMonthYear, showYearOnly, showMonthOnly]);

  // Close time picker on outside click (trigger or portal menu)
  React.useEffect(() => {
    if (!showTimeOnly || !timePickerOpen) return;
    function handleClickOutside(e) {
      const inTrigger = timePickerRef.current && timePickerRef.current.contains(e.target);
      const inMenu = timeMenuRef.current && timeMenuRef.current.contains(e.target);
      if (!inTrigger && !inMenu) setTimePickerOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => document.removeEventListener('mousedown', handleClickOutside, true);
  }, [showTimeOnly, timePickerOpen]);

  // Position datetime popup when open
  useLayoutEffect(() => {
    if (!showDateTime || !dateTimeOpen || !dateTimeTriggerRef.current) return;
    const update = () => {
      if (dateTimeTriggerRef.current) {
        const rect = dateTimeTriggerRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
        const popupWidth = Math.min(
          Math.max(rect.width, DATETIME_MENU_MIN_WIDTH),
          Math.max(DATETIME_MENU_MIN_WIDTH, viewportWidth - 16)
        );
        setDateTimeMenuPosition(computePopupPosition(rect, popupWidth, DATETIME_MENU_ESTIMATED_HEIGHT));
      }
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
      setDateTimeMenuPosition(null);
    };
  }, [dateTimeOpen, showDateTime]);

  // Close datetime picker on outside click
  React.useEffect(() => {
    if (!showDateTime || !dateTimeOpen) return;
    function handleClickOutside(e) {
      const inTrigger = dateTimeTriggerRef.current && dateTimeTriggerRef.current.contains(e.target);
      const inMenu = dateTimeMenuRef.current && dateTimeMenuRef.current.contains(e.target);
      if (!inTrigger && !inMenu) setDateTimeOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => document.removeEventListener('mousedown', handleClickOutside, true);
  }, [showDateTime, dateTimeOpen]);

  const showPeriodPicker = showMonthYear || showYearOnly || showMonthOnly;
  const parsedPeriod = parsePeriodValue(value);
  const [periodOpen, setPeriodOpen] = useState(false);
  const [periodMenuPosition, setPeriodMenuPosition] = useState(null);
  const [periodView, setPeriodView] = useState(showYearOnly ? 'year' : 'month');
  const [periodMonth, setPeriodMonth] = useState(parsedPeriod.month != null ? parsedPeriod.month : dayjs().month());
  const [periodYear, setPeriodYear] = useState(parsedPeriod.year != null ? parsedPeriod.year : dayjs().year());
  const periodTriggerRef = useRef(null);
  const periodMenuRef = useRef(null);
  const yearGridRef = useRef(null);
  const selectedYearRef = useRef(null);

  React.useEffect(() => {
    if (!showPeriodPicker) return;
    const next = parsePeriodValue(value);
    if (next.month != null) setPeriodMonth(next.month);
    if (next.year != null) {
      setPeriodYear(next.year);
    }
  }, [showPeriodPicker, value]);

  useLayoutEffect(() => {
    if (!showPeriodPicker || !periodOpen || !periodTriggerRef.current) return;
    const update = () => {
      if (!periodTriggerRef.current) return;
      const rect = periodTriggerRef.current.getBoundingClientRect();
      const popupWidth = Math.max(rect.width, 240);
      setPeriodMenuPosition(computePopupPosition(rect, popupWidth, PERIOD_MENU_ESTIMATED_HEIGHT));
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
      setPeriodMenuPosition(null);
    };
  }, [showPeriodPicker, periodOpen]);

  React.useEffect(() => {
    if (!showPeriodPicker || !periodOpen) return;
    function handleClickOutside(e) {
      const inTrigger = periodTriggerRef.current && periodTriggerRef.current.contains(e.target);
      const inMenu = periodMenuRef.current && periodMenuRef.current.contains(e.target);
      if (!inTrigger && !inMenu) setPeriodOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => document.removeEventListener('mousedown', handleClickOutside, true);
  }, [showPeriodPicker, periodOpen]);

  React.useLayoutEffect(() => {
    if (!periodOpen) return;
    if (periodView !== 'year' && !showYearOnly) return;
    const grid = yearGridRef.current;
    const node = selectedYearRef.current;
    if (!grid || !node) return;
    const gridRect = grid.getBoundingClientRect();
    const nodeRect = node.getBoundingClientRect();
    grid.scrollTop += nodeRect.top - gridRect.top - (gridRect.height / 2) + (nodeRect.height / 2);
  }, [periodOpen, periodView, showYearOnly, periodYear]);

  const periodDisplayValue = (() => {
    if (showMonthYear && parsedPeriod.year != null && parsedPeriod.month != null) {
      return dayjs().year(parsedPeriod.year).month(parsedPeriod.month).format('MMMM YYYY');
    }
    if (showYearOnly && parsedPeriod.year != null) return String(parsedPeriod.year);
    if (showMonthOnly && parsedPeriod.month != null) {
      return dayjs().month(parsedPeriod.month).format('MMMM');
    }
    return '';
  })();

  const periodPlaceholder = placeholder
    || (showMonthYear ? 'MMMM YYYY' : showYearOnly ? 'YYYY' : 'MMMM');

  const openPeriodPicker = () => {
    if (disabled) return;
    setPeriodView(showYearOnly ? 'year' : 'month');
    const next = parsePeriodValue(value);
    const year = next.year != null ? next.year : dayjs().year();
    const month = next.month != null ? next.month : dayjs().month();
    setPeriodYear(year);
    setPeriodMonth(month);
    setPeriodOpen((openState) => !openState);
  };

  const commitPeriod = (month, year) => {
    if (showMonthYear) {
      emitChange(`${year}-${String(month + 1).padStart(2, '0')}`);
    } else if (showYearOnly) {
      emitChange(String(year));
    } else if (showMonthOnly) {
      emitChange(String(month + 1).padStart(2, '0'));
    }
    setPeriodOpen(false);
  };

  if (showPeriodPicker) {
    const showYearGrid = periodView === 'year' || showYearOnly;
    const headerLabel = showYearGrid
      ? 'Select year'
      : (showMonthOnly ? 'Select month' : dayjs().year(periodYear).month(periodMonth).format('MMMM YYYY'));

    return (
      <div className="custom-datepicker-wrapper" style={{ width, position: 'relative', ...style }}>
        {label && (
          <label style={{ display: 'block', marginBottom: 4, color: 'var(--color-text)', fontWeight: 500, fontSize: '14px', textAlign: 'left', fontFamily: 'inherit' }}>
            {label}
          </label>
        )}
        <button
          type="button"
          ref={periodTriggerRef}
          className="cdp-field-trigger cdp-period-trigger"
          disabled={disabled}
          aria-required={effectiveRequired}
          aria-haspopup="dialog"
          aria-expanded={periodOpen}
          aria-label={label || periodPlaceholder}
          onClick={openPeriodPicker}
          style={{
            width: '100%',
            height: FIELD_TRIGGER_HEIGHT,
            minHeight: FIELD_TRIGGER_HEIGHT,
            border: `1px solid ${error ? 'var(--color-error, #d32f2f)' : 'var(--color-border-input)'}`,
            background: disabled ? 'var(--color-bg-alt)' : 'var(--color-bg-input)',
            color: periodDisplayValue ? 'var(--color-text)' : 'var(--color-text-secondary)',
            fontFamily: 'inherit',
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.6 : 1,
            outline: 'none',
          }}
        >
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {periodDisplayValue || periodPlaceholder}
          </span>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="3" y="5" width="18" height="16" rx="3"/>
            <path d="M16 3v4M8 3v4M3 9h18"/>
          </svg>
        </button>
        {helperText && <div style={{ marginTop: 4, fontSize: '12px', color: error ? 'var(--color-error, #d32f2f)' : 'var(--color-text-secondary)' }}>{helperText}</div>}
        {periodOpen && periodMenuPosition && typeof document !== 'undefined' && createPortal(
          <div
            ref={periodMenuRef}
            className="cdp2-calendar cdp2-period-calendar"
            style={{
              ...popupPanelStyle(periodMenuPosition),
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              height: showYearGrid
                ? Math.min(PERIOD_MENU_ESTIMATED_HEIGHT, periodMenuPosition.maxHeight || PERIOD_MENU_ESTIMATED_HEIGHT)
                : undefined,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12, flexShrink: 0 }}>
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  if (!showMonthOnly && !showYearOnly) {
                    setPeriodView((view) => (view === 'year' ? 'month' : 'year'));
                  }
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--color-text)',
                  fontWeight: 600,
                  fontSize: 16,
                  cursor: showMonthOnly || showYearOnly ? 'default' : 'pointer',
                  fontFamily: 'inherit',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                {headerLabel}
                {!showMonthOnly && !showYearOnly && (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d={showYearGrid ? 'M6 15l6-6 6 6' : 'M6 9l6 6 6-6'} />
                  </svg>
                )}
              </button>
            </div>
            {showYearGrid ? (
              <div
                ref={yearGridRef}
                className="cdp-year-scroll"
                onWheel={(e) => e.stopPropagation()}
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                  gap: 8,
                  overflowY: 'auto',
                  minHeight: 0,
                  flex: '1 1 auto',
                  paddingRight: 4,
                }}
              >
                {PERIOD_YEARS.map((year) => (
                  <button
                    key={year}
                    type="button"
                    ref={year === periodYear ? selectedYearRef : null}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      setPeriodYear(year);
                      if (showYearOnly) {
                        commitPeriod(periodMonth, year);
                      } else {
                        setPeriodView('month');
                      }
                    }}
                    style={pickerCellStyle(year === periodYear)}
                  >
                    {year}
                  </button>
                ))}
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8, flexShrink: 0 }}>
                {MONTH_SHORT_LABELS.map((name, monthIndex) => (
                  <button
                    key={name}
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      setPeriodMonth(monthIndex);
                      commitPeriod(monthIndex, periodYear);
                    }}
                    style={pickerCellStyle(monthIndex === periodMonth)}
                  >
                    {name}
                  </button>
                ))}
              </div>
            )}
          </div>,
          document.body
        )}
      </div>
    );
  }

  // When showTimeOnly, render custom time picker (styled like app, full-width dark dropdown)
  if (showTimeOnly) {
    return (
      <div ref={timePickerRef} className="custom-datepicker-wrapper custom-time-picker-wrapper" style={{ width, position: 'relative', ...style }}>
        {label && (
          <label
            style={{
              display: 'block',
              marginBottom: 4,
              color: 'var(--color-text)',
              fontWeight: 500,
              fontSize: '14px',
              textAlign: 'left',
              fontFamily: 'inherit',
            }}
          >
            {label}
          </label>
        )}
        <div
          ref={timeTriggerRef}
          className="cdp-field-shell"
          style={{
            width: '100%',
            height: FIELD_TRIGGER_HEIGHT,
            minHeight: FIELD_TRIGGER_HEIGHT,
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            borderRadius: '8px',
            border: `1px solid ${error ? 'var(--color-error, #d32f2f)' : 'var(--color-border-input)'}`,
            background: 'var(--color-bg-input)',
            boxSizing: 'border-box',
          }}
        >
          <input
            type="text"
            value={timeInputValue}
            onChange={handleTimeInputChange}
            onBlur={handleTimeInputBlur}
            onKeyDown={handleTimeInputKeyDown}
            placeholder="--:-- --"
            disabled={disabled}
            required={effectiveRequired}
            aria-required={effectiveRequired}
            style={{
              flex: 1,
              width: '100%',
              height: '100%',
              padding: '8px 38px 8px 16px',
              border: 'none',
              borderRadius: '8px',
              background: 'transparent',
              color: timeInputValue ? 'var(--color-text)' : 'var(--color-text-secondary)',
              fontSize: '15px',
              fontFamily: 'inherit',
              fontWeight: 400,
              outline: 'none',
              opacity: disabled ? 0.6 : 1,
              boxSizing: 'border-box',
            }}
          />
          <button
            type="button"
            tabIndex={-1}
            disabled={disabled}
            onClick={(e) => { e.preventDefault(); handleTimePickerClick(); }}
            onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
            style={{
              position: 'absolute',
              right: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'none',
              border: 'none',
              padding: 6,
              margin: 0,
              cursor: disabled ? 'not-allowed' : 'pointer',
              borderRadius: '8px',
              outline: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--color-text)',
            }}
            aria-label="Open time picker"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <path d="M12 6v6l4 2"/>
            </svg>
          </button>
        </div>
        {helperText && <div style={{ marginTop: 4, fontSize: '12px', color: 'var(--color-error, #d32f2f)' }}>{helperText}</div>}
        {timePickerOpen && timeMenuPosition && typeof document !== 'undefined' && createPortal(
          <div
            ref={timeMenuRef}
            className="cdp2-calendar"
            style={{
              ...popupPlacementStyle(timeMenuPosition),
              zIndex: 10000,
              background: 'var(--color-bg-alt)',
              color: 'var(--color-text)',
              border: '2px solid var(--color-accent)',
              borderRadius: 12,
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
              padding: 16,
              fontFamily: 'inherit',
              boxSizing: 'border-box',
              overflowY: 'auto',
            }}
          >
            <ShortTimeLists
              hour12={timeHour}
              minute={timeMinute}
              amPm={timeAmPm}
              onChange={(hour, minute, ap) => {
                setTimeHour(hour);
                setTimeMinute(minute);
                setTimeAmPm(ap);
                applyTimeSelection(hour, minute, ap);
              }}
            />
          </div>,
          document.body
        )}
      </div>
    );
  }

  // DateTime mode: one input showing "MM/DD/YYYY h:mm A", popup with calendar + time
  const formatDateTimeDisplay = (v) => (v && dayjs(v).isValid() ? dayjs(v).format('MM/DD/YYYY h:mm A') : '');
  const applyDateTimeChange = (d) => {
    if (d && d.isValid()) {
      setDateTimeWorking(d);
      const h24 = d.hour();
      setDtHour(h24 === 0 ? 12 : h24 > 12 ? h24 - 12 : h24);
      setDtMinute(d.minute());
      setDtAmPm(h24 < 12 ? 'AM' : 'PM');
      emitChange(d.toDate());
    }
  };
  const handleDateTimeDayClick = (date) => {
    const combined = date.hour(dtAmPm === 'PM' && dtHour !== 12 ? dtHour + 12 : dtAmPm === 'AM' && dtHour === 12 ? 0 : dtHour).minute(dtMinute).second(0).millisecond(0);
    applyDateTimeChange(combined);
    setDtCalendarMonth(date.month());
    setDtCalendarYear(date.year());
  };
  const handleDateTimeTimeChange = (hour12, minute, amPm) => {
    let h24 = hour12;
    if (amPm === 'PM' && hour12 !== 12) h24 = hour12 + 12;
    if (amPm === 'AM' && hour12 === 12) h24 = 0;
    const combined = dateTimeWorking.hour(h24).minute(minute).second(0).millisecond(0);
    applyDateTimeChange(combined);
  };

  if (showDateTime) {
    const startOfMonthDt = dayjs().year(dtCalendarYear).month(dtCalendarMonth).date(1);
    const startDayDt = startOfMonthDt.day();
    const weeksDt = [];
    let currentDt = startOfMonthDt.subtract(startDayDt, 'day');
    for (let w = 0; w < 6; w++) {
      const week = [];
      for (let d = 0; d < 7; d++) {
        week.push(currentDt);
        currentDt = currentDt.add(1, 'day');
      }
      weeksDt.push(week);
    }

    return (
      <div className="custom-datepicker-wrapper custom-time-picker-wrapper" style={{ width, position: 'relative', ...style }}>
        {label && (
          <label style={{ display: 'block', marginBottom: 4, color: 'var(--color-text)', fontWeight: 500, fontSize: '14px', textAlign: 'left', fontFamily: 'inherit' }}>
            {label}
          </label>
        )}
        <button
          type="button"
          ref={dateTimeTriggerRef}
          className="cdp-field-trigger"
          disabled={disabled}
          aria-required={effectiveRequired}
          aria-haspopup="dialog"
          aria-expanded={dateTimeOpen}
          aria-label={label || placeholder || 'Date and time'}
          onClick={() => !disabled && setDateTimeOpen((o) => !o)}
          style={{
            width: '100%',
            height: FIELD_TRIGGER_HEIGHT,
            minHeight: FIELD_TRIGGER_HEIGHT,
            border: `1px solid ${error ? 'var(--color-error, #d32f2f)' : 'var(--color-border-input)'}`,
            background: disabled ? 'var(--color-bg-alt)' : 'var(--color-bg-input)',
            color: formatDateTimeDisplay(value) ? 'var(--color-text)' : 'var(--color-text-secondary)',
            fontFamily: 'inherit',
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.6 : 1,
            outline: 'none',
          }}
        >
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {formatDateTimeDisplay(value) || placeholder}
          </span>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="3" y="5" width="18" height="16" rx="3"/>
            <path d="M16 3v4M8 3v4M3 9h18"/>
          </svg>
        </button>
        {helperText && <div style={{ marginTop: 4, fontSize: '12px', color: 'var(--color-error, #d32f2f)' }}>{helperText}</div>}
        {dateTimeOpen && dateTimeMenuPosition && typeof document !== 'undefined' && createPortal(
          <div
            ref={dateTimeMenuRef}
            className="cdp2-calendar cdp2-datetime-calendar"
            style={{
              ...popupPlacementStyle(dateTimeMenuPosition),
              zIndex: 10000,
              background: 'var(--color-bg-alt)',
              color: 'var(--color-text)',
              border: '2px solid var(--color-accent)',
              borderRadius: 12,
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
              padding: 16,
              fontFamily: 'inherit',
              boxSizing: 'border-box',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div className="cdp-datetime-body">
              <div className="cdp-datetime-cal">
                <div className="cdp-datetime-cal-header">
                  <span className="cdp-datetime-cal-title">{dayjs().year(dtCalendarYear).month(dtCalendarMonth).format('MMMM YYYY')}</span>
                  <div className="cdp-datetime-cal-nav">
                    <button
                      type="button"
                      className="cdp-cal-nav-btn"
                      aria-label="Previous month"
                      onMouseDown={(e) => { e.preventDefault(); if (dtCalendarMonth === 0) { setDtCalendarMonth(11); setDtCalendarYear((y) => y - 1); } else setDtCalendarMonth((m) => m - 1); }}
                    >&lt;</button>
                    <button
                      type="button"
                      className="cdp-cal-nav-btn"
                      aria-label="Next month"
                      onMouseDown={(e) => { e.preventDefault(); if (dtCalendarMonth === 11) { setDtCalendarMonth(0); setDtCalendarYear((y) => y + 1); } else setDtCalendarMonth((m) => m + 1); }}
                    >&gt;</button>
                  </div>
                </div>
                <div className="cdp-datetime-dow">
                  {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((dn, i) => (
                    <div key={`${dn}-${i}`}>{dn}</div>
                  ))}
                </div>
                <div className="cdp-datetime-grid">
                  {weeksDt.map((week) => week.map((date) => {
                    const isSelected = dateTimeWorking && date.isSame(dateTimeWorking, 'day');
                    const isTodayDt = date.isToday();
                    const isCurrentMonth = date.month() === dtCalendarMonth;
                    return (
                      <button
                        key={date.format('YYYY-MM-DD')}
                        type="button"
                        className={`cdp-day-cell${isSelected ? ' is-selected' : ''}${isTodayDt && !isSelected ? ' is-today' : ''}${isCurrentMonth ? '' : ' is-outside'}`}
                        onMouseDown={(e) => { e.preventDefault(); handleDateTimeDayClick(date); }}
                      >
                        {date.date()}
                      </button>
                    );
                  }))}
                </div>
              </div>
              <div className="cdp-datetime-time">
                <ShortTimeLists
                  variant="inline"
                  hour12={dtHour}
                  minute={dtMinute}
                  amPm={dtAmPm}
                  onChange={(hour, minute, ap) => {
                    setDtHour(hour);
                    setDtMinute(minute);
                    setDtAmPm(ap);
                    handleDateTimeTimeChange(hour, minute, ap);
                  }}
                />
              </div>
            </div>
          </div>,
          document.body
        )}
      </div>
    );
  }

  // Calendar navigation
  const handlePrevMonth = () => {
    if (calendarMonth === 0) {
      setCalendarMonth(11);
      setCalendarYear(y => y - 1);
    } else {
      setCalendarMonth(m => m - 1);
    }
  };
  const handleNextMonth = () => {
    if (calendarMonth === 11) {
      setCalendarMonth(0);
      setCalendarYear(y => y + 1);
    } else {
      setCalendarMonth(m => m + 1);
    }
  };

  // Handle day click
  const handleDayClick = (date) => {
    if (disabled) return;
    
    // If the clicked date is from a different month, update the calendar view
    if (date.month() !== calendarMonth || date.year() !== calendarYear) {
      setCalendarMonth(date.month());
      setCalendarYear(date.year());
    }
    
    // Format the date in local time to avoid timezone issues
    const localDate = dayjs(date.format('YYYY-MM-DD'));
    setTypedValue(null);
    emitChange(localDate.format(dayjsFormat));
    setOpen(false);
  };

  // Generate calendar grid
  const startOfMonth = dayjs().year(calendarYear).month(calendarMonth).date(1);
  const endOfMonth = startOfMonth.endOf('month');
  const daysInMonth = endOfMonth.date();
  const startDay = startOfMonth.day(); // 0 (Sun) - 6 (Sat)
  const weeks = [];
  let current = startOfMonth.subtract(startDay, 'day');
  for (let w = 0; w < 6; w++) {
    const week = [];
    for (let d = 0; d < 7; d++) {
      week.push(current);
      current = current.add(1, 'day');
    }
    weeks.push(week);
  }
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="custom-datepicker-wrapper" style={{ width, position: 'relative', overflow: 'visible', ...style }}>
      {label && (
        <label
          style={{
            display: 'block',
            marginBottom: 4,
            color: 'var(--color-text)',
            fontWeight: 500,
            fontSize: '14px',
            textAlign: 'left',
            fontFamily: 'inherit',
          }}
        >
          {label}
        </label>
      )}
      <div ref={inputRef} className="custom-datepicker-input-wrapper" style={{ position: 'relative', width: '100%', overflow: 'visible' }}>
        <input
          ref={textInputRef}
          type="text"
          value={displayValue}
          onChange={e => {
            let val = e.target.value;
            if (/^\d{8}$/.test(val)) {
              val = `${val.slice(0, 2)}/${val.slice(2, 4)}/${val.slice(4)}`;
            }
            setTypedValue(val);
            emitChange(val);
          }}
          onFocus={() => {
            if (typedValue == null) setTypedValue(formattedValue);
            handleInputFocus();
          }}
          onClick={handleInputFocus}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              e.stopPropagation();
              commitTypedDate(e.target.value);
              setTypedValue(null);
              setOpen(false);
            }
            if (e.key === 'Escape') {
              setTypedValue(null);
              setOpen(false);
            }
          }}
          placeholder={placeholder}
          disabled={disabled}
          required={effectiveRequired}
          aria-required={effectiveRequired}
          style={{
            width: '100%',
            height,
            padding: '8px 38px 8px 16px', // right padding for icon
            borderRadius: '8px',
            border: `1px solid ${error ? 'var(--color-error, #d32f2f)' : 'var(--color-border-input)'}`,
            background: 'var(--color-bg-input)',
            color: 'var(--color-text)',
            fontSize: '15px',
            fontFamily: 'inherit',
            fontWeight: 400,
            outline: 'none',
            opacity: disabled ? 0.6 : 1,
            transition: 'border 0.2s',
            boxSizing: 'border-box',
          }}
          onBlur={e => {
            const val = e.target.value;
            // Don't close if this blur was caused by clicking inside the calendar (prev/next or day) - works on first open when relatedTarget can be unreliable
            if (blurFromCalendarClickRef.current) {
              blurFromCalendarClickRef.current = false;
              return;
            }
            const related = e.relatedTarget;
            const focusMovedToCalendar = related && calendarRef.current && calendarRef.current.contains(related);
            const focusMovedToTrigger = related && inputRef.current && inputRef.current.contains(related);
            if (focusMovedToCalendar || focusMovedToTrigger) return;
            commitTypedDate(val);
            setTypedValue(null);
            setTimeout(() => {
              const ae = document.activeElement;
              const insideCalendar = calendarRef.current && calendarRef.current.contains(ae);
              const insideTrigger = inputRef.current && inputRef.current.contains(ae);
              if (!insideCalendar && !insideTrigger) setOpen(false);
            }, 150);
          }}
        />
        {/* Calendar Icon Button */}
        <button
          type="button"
          tabIndex={-1}
          disabled={disabled}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (disabled) return;
            setOpen((o) => {
              if (!o) syncCalendarToValue();
              return !o;
            });
          }}
          style={{
            position: 'absolute',
            right: 8,
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'none',
            border: 'none',
            padding: 6,
            margin: 0,
            cursor: disabled ? 'not-allowed' : 'pointer',
            borderRadius: '8px',
            outline: 'none',
            boxShadow: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 36,
            height: 36,
            transition: 'box-shadow 0.2s',
            overflow: 'visible',
          }}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="var(--color-text)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              display: 'block',
              flexShrink: 0,
            }}
          >
            <rect x="3" y="5" width="18" height="16" rx="3"/>
            <path d="M16 3v4M8 3v4M3 9h18"/>
          </svg>
        </button>
        {/* Calendar Popup - Rendered in portal with fixed position to avoid form scrollbar */}
        {open && calendarPosition && typeof document !== 'undefined' && createPortal(
          <div
            ref={calendarRef}
            className="cdp2-calendar"
            onMouseDown={() => {
              blurFromCalendarClickRef.current = true;
            }}
            style={{
              ...popupPlacementStyle(calendarPosition),
              minWidth: CALENDAR_MIN_WIDTH,
              zIndex: 10000,
              background: 'var(--color-bg-alt)',
              color: 'var(--color-text)',
              border: '2px solid var(--color-accent)',
              borderRadius: 12,
              boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
              padding: 16,
              fontFamily: 'inherit',
              boxSizing: 'border-box',
              overflowY: 'auto',
            }}
            tabIndex={-1}
          >
            {/* Calendar navigation */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <button type="button" onMouseDown={(e) => { e.preventDefault(); handlePrevMonth(); }} style={{ background: 'none', border: 'none', color: 'var(--color-accent)', fontSize: 18, cursor: 'pointer', padding: 4, borderRadius: 6 }}>&lt;</button>
              <span style={{ fontWeight: 600, fontSize: 16, color: 'var(--color-text)' }}>{dayjs().year(calendarYear).month(calendarMonth).format('MMMM YYYY')}</span>
              <button type="button" onMouseDown={(e) => { e.preventDefault(); handleNextMonth(); }} style={{ background: 'none', border: 'none', color: 'var(--color-accent)', fontSize: 18, cursor: 'pointer', padding: 4, borderRadius: 6 }}>&gt;</button>
            </div>
            {/* Day of week headers */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: 4 }}>
              {dayNames.map(dn => (
                <div key={dn} style={{ textAlign: 'center', color: 'var(--color-text-secondary)', fontWeight: 500, fontSize: 13 }}>{dn}</div>
              ))}
            </div>
            {/* Calendar grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
              {weeks.map((week, wi) => week.map((date, di) => {
                const isSelected = selectedDate && date.isSame(selectedDate, 'day');
                const isToday = date.isToday();
                const isCurrentMonth = date.month() === calendarMonth;
                return (
                  <div
                    key={date.format('YYYY-MM-DD')}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleDayClick(date);
                    }}
                    style={{
                      textAlign: 'center',
                      padding: '6px 0',
                      color: isCurrentMonth ? 'var(--color-text)' : 'var(--color-text-secondary)',
                      background: isSelected
                        ? 'var(--color-accent)'
                        : isToday
                          ? 'rgba(33,150,243,0.08)'
                          : 'none',
                      borderRadius: 6,
                      fontWeight: isSelected ? 700 : isToday ? 600 : 400,
                      fontSize: 13,
                      cursor: 'pointer',
                      transition: 'background 0.15s',
                      boxShadow: isSelected ? '0 2px 8px rgba(33,150,243,0.10)' : 'none',
                      color: isSelected ? '#fff' : isCurrentMonth ? 'var(--color-text)' : 'var(--color-text-secondary)',
                      border: '1px solid var(--color-border)',
                      margin: '1px'
                    }}
                  >
                    {date.date()}
                  </div>
                );
              }))}
            </div>
          </div>,
          document.body
        )}
        {helperText && <div style={{ marginTop: 4, fontSize: '12px', color: 'var(--color-error, #d32f2f)' }}>{helperText}</div>}
      </div>
    </div>
  );
};

export default CustomDatePicker; 




